import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { ZFields } from '@common/data-models/fields/z-fields';
import { ViewStateFilter } from '@common/data-models/navigation/viewstate-filter';
import { SearchViewStateRecord } from '@common/data-models/records/search-viewstate-record';
import { SingleViewStateRecord } from '@common/data-models/records/single-viewstate-record';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ObjectTypes } from '@common/enums/object-types';
import { ViewStateTypes, ViewStateTypesEnum } from '@common/enums/viewstate-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { ViewStateRecordsAlias } from '@common/services/layout-state';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { FilterDef } from '@src/suite/common/data-models/navigation/filter-def';
import { NOMDatabase } from '@src/suite/common/store/records/records.models';
import { Util } from '@src/suite/common/utils/util';
import { filterFilterByObjectTypes } from '@src/suite/common/utils/viewstate-utils';
import { ViewStateOptions } from '@suite/common/data-models/navigation/viewstate-options';

@Injectable({ providedIn: 'root' })
export class LiveSearchService {
    private nomWebServicesJson: NomWebServicesJson = inject(NomWebServicesJson);
    private globalTablesService: GlobalTablesService = inject(GlobalTablesService);
    private visualStateService: VisualStateService = inject(VisualStateService);

    public checkForTypePrivs(objectType: ObjectTypes): Array<number> {
        const typesArray = [];
        if (objectType === ObjectTypes.RUNDOWN || objectType === ObjectTypes.UNASSIGNED) {
            typesArray.push(ObjectTypes.RUNDOWN);
        }
        return typesArray;
    }

    public async getFilteredResultsGuids(vs: ViewStateRecordsAlias, database: NOMDatabase): Promise<ServiceResponse<Array<{ [id: string]: any }>>> {
        const ret: Array<{ [id: string]: any }> = [];

        const objectType = this.getObjectTypeByViewStateType(vs.viewStateType);
        const singleViewState = <SingleViewStateRecord>vs;
        const searchViewState = <SearchViewStateRecord>vs;
        const requestParams = await this.buildFilterServiceParams(
            singleViewState.viewStateFilter,
            true,
            { Value: 'CreatedDateTime' },
            'desc',
            database,
            objectType,
            searchViewState.viewStateSearchQuery,
            false
        );

        requestParams.FieldsToReturn = ['guid', 'type', PSTFieldNames.PSTDesk, 'CreatedDateTime'];
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post('/api/SearchFields', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            Util.saveWarningError(`Failed to load filtered results! StackTrace: NomWebServicesJson.getFilteredResultsGuids(); Error:${error.toString()}`);
            return new ServiceResponse<Array<{ [id: string]: any }>>(false, ret);
        }

        if (response) {
            const respObj = response.body;
            for (const p of respObj.SearchResults) {
                const obj: { [id: string]: any } = {};
                for (const item of p) {
                    obj[item.FieldName] = item.FieldValue;
                }
                ret.push(obj);
            }
            return new ServiceResponse<Array<{ [id: string]: any }>>(true, ret);
        }
        return new ServiceResponse<Array<{ [id: string]: any }>>(false, 'Failed to load updated guid list from web service');
    }

    public async getSearchResults(vs: ViewStateRecordsAlias, database: NOMDatabase, guid?: string): Promise<ServiceResponse<LiveSearchResult>> {
        const rundownResults = new Map<string, StorytellingRundownRecord>();
        const storiesResults = new Map<string, StorytellingStoryRecord>();
        const listGuids = new Set<string>();
        let response: HttpResponse<any> = null;

        const objectType = this.getObjectTypeByViewStateType(vs.viewStateType, guid);
        const singleViewState = <SingleViewStateRecord>vs;
        const searchViewState = <SearchViewStateRecord>vs;

        const rundownRequestParams = this.buildFilterServiceParams(
            singleViewState.viewStateFilter,
            true,
            singleViewState.viewStateSorting,
            singleViewState.viewStateSortOrder,
            database,
            objectType,
            searchViewState.viewStateSearchQuery,
            false,
            guid
        );
        rundownRequestParams.IncludeChildren = true;
        rundownRequestParams['FieldsToExclude'] = ['PSTViewStateScreenshot', 'backfields'];

        try {
            response = await this.nomWebServicesJson.post('/api/v2/Search', rundownRequestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load search results!',
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
            });
            return new ServiceResponse<LiveSearchResult>(false, {
                rundowns: new Map<string, StorytellingRundownRecord>(),
                stories: new Map<string, StorytellingStoryRecord>(),
                listGuids: new Set<string>(),
            });
        }
        if (response) {
            const respObj = response.body;
            for (const p of respObj.SearchResults) {
                const serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                const record = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingRundownRecord>(serviceCollectionRecord);
                rundownResults.set(record.Guid, record);
                listGuids.add(record.Guid);
                for (const childScript of p.CollectionScripts) {
                    const story = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingStoryRecord>(childScript);
                    storiesResults.set(story.Guid, story);
                }
            }
        }
        return new ServiceResponse<LiveSearchResult>(true, {
            rundowns: rundownResults,
            stories: storiesResults,
            listGuids,
        });
    }

    private getObjectTypeByViewStateType(vsType: ViewStateTypesEnum, guid?: string) {
        if (vsType === ViewStateTypes.ROLIST) {
            return ObjectTypes.RUNDOWN;
        }
        if (guid) {
            return ObjectTypes.UNASSIGNED;
        }
        return null;
    }

    protected buildFilterServiceParams(
        filter: ViewStateFilter,
        applyPrivs: boolean,
        sortBy: any,
        sortOrder: string,
        database: NOMDatabase,
        objectType?: number,
        search?: string,
        poller?: boolean,
        guid?: string
    ): any {
        let QueryTerms: string = '';
        if (search) {
            QueryTerms = search;
        }

        filter = filter ? filter.deepCopy() : new ViewStateFilter();

        const zFields = new ZFields(true);
        // \\\\W\\\\${this.nomWebServicesJson.PSTHoldFolder}
        let objTypes = [];
        if (applyPrivs) {
            if (guid) {
                objTypes = this.checkForTypePrivs(ObjectTypes.UNASSIGNED);
                zFields.add('type__list__', objTypes.join(';'));
            } else if (objectType) {
                objTypes = this.checkForTypePrivs(objectType);
                zFields.add('type', objTypes.join(';'));
            } else {
                objTypes = this.checkForTypePrivs(ObjectTypes.UNASSIGNED);
                zFields.add('type__list__', objTypes.join(';'));
            }
        } else if (objectType) {
            zFields.add('type', objectType);
        }

        const queryTermTags: Array<string> = [];
        const queryTermUsers: Array<string> = [];
        const queryTermWords: Array<string> = [];

        let queryTermsArr = [];

        if (QueryTerms.startsWith('#')) {
            queryTermTags.push(QueryTerms.substring(1));
        } else {
            // we should support search for tags which consists of more than one word
            queryTermsArr = QueryTerms.split(' ');
        }
        for (const term of queryTermsArr) {
            if (term.startsWith('@')) {
                queryTermUsers.push(term.substr(1));
            } else {
                queryTermWords.push(term);
            }
        }

        if (queryTermTags.length > 0) {
            const values: Array<string> = [];
            if (filter.map[PSTFieldNames.PSTTags] && filter.map[PSTFieldNames.PSTTags].Value && Array.isArray(filter.map[PSTFieldNames.PSTTags].Value)) {
                for (const v of <Array<string>>filter.map[PSTFieldNames.PSTTags].Value) {
                    if (values.indexOf(v) < 0) {
                        values.push(v);
                    }
                }
            }
            if (queryTermTags.length > 0) {
                for (const termTag of queryTermTags) {
                    if (values.indexOf(termTag) < 0) {
                        values.push(termTag);
                    }
                }
            }
            if (values.length > 0) {
                filter.push(FilterDef.create(PSTFieldNames.PSTTags, 'LOOKUPLIST', values));
            }
        }

        if (queryTermUsers.length > 0) {
            for (const field of this.globalTablesService.FieldDefs) {
                const isStaffIDList = this.globalTablesService.isStaffIDListFieldType(field.ID);
                if (isStaffIDList) {
                    if (queryTermUsers.length > 0) {
                        for (const termUser of queryTermUsers) {
                            filter.groups.push(FilterDef.create(field.ID, 'STAFFIDLIST', termUser));
                        }
                    }
                }
            }
        }
        this.fillZFieldsFromFilter(objTypes, zFields, filter, guid);
        let sortFields = [];
        if (!guid) {
            sortFields = this.buildSortFields(objectType, sortBy, sortOrder);
        }

        return {
            Database: database.Database,
            QueryTerms: queryTermWords.length === 0 ? '' : queryTermWords.join(' '),
            zFields: zFields.data,
            NOMLocations: database.NOMLocations,
            NOMContentTypes: {
                All: false,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: true,
                Scripts: false,
                Storyboards: false,
                Video: false,
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: '1800-02-28T22:00:00.000Z',
                ItemDateTo: '1800-02-28T22:00:00.000Z',
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false,
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: '1800-02-28T22:00:00.000Z',
                    ItemDateTo: '1800-02-28T22:00:00.000Z',
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false,
                },
                ExtendedWireBriefing: true,
            },
            // MaxRows: maxResults,
            MaxRows: this.visualStateService.ConfigSettings.MaxRows,
            SortByRank: false,
            SortFields: sortFields,
            ExactMatch: false,
        };
    }

    protected buildSortFields(objectType: any, sortByObj: ViewStateOptions, sortOrder: string): Array<{ [id: string]: string }> {
        let sortBy = sortByObj.Value;
        if (sortBy) {
            // strip quotes before applying to search query
            sortBy = sortBy.replace(/"/g, '');
            // replace -date -time parts
            sortBy = sortBy.replace(/(-.*)/g, '');
        }
        if (sortBy === PSTFieldNames.PSTModTime) sortBy = 'modTime';
        const sortFields = [];
        if (sortBy && sortOrder) {
            sortFields.push({ FieldName: sortBy, FieldValue: sortOrder });
        }
        sortFields.push({ FieldName: 'guid', FieldValue: 'asc' });
        return sortFields;
    }

    protected fillZFieldsFromFilter(objTypes: Array<number>, zFields: ZFields, filterObj: ViewStateFilter, guid?: string) {
        const filters = filterFilterByObjectTypes(filterObj, objTypes, this.globalTablesService);

        if (!guid) {
            for (const group of filters.groups) {
                const value = group.group();
                if (value) {
                    zFields.add(this.resolveFieldName(value.FieldName), value.FieldValue);
                }
            }

            for (const filter of filters.filters) {
                if (filter.ID === PSTFieldNames.PSTServerLocations) continue;
                if (filter.NotUsed) {
                    // neither in the required nor in the optional list
                    continue;
                }
                const value = filter.get();

                if (value) {
                    let fieldName = value.FieldName;
                    if (filter.ID === PSTFieldNames.PSTROModTime) {
                        fieldName = fieldName.replace('RO', '');
                    }
                    zFields.add(this.resolveFieldName(fieldName), value.FieldValue);
                }
            }
        }
        if (guid) {
            zFields.add('guid', guid);
        }

        return zFields;
    }

    private resolveFieldName(name: string): string {
        if (!name || typeof name !== 'string') {
            return name;
        }
        // if ModTime is in the field name lower the first letter
        return name.startsWith('ModTime') ? name.charAt(0).toLowerCase() + name.slice(1) : name;
    }
}
